import { assignmentFormSchema } from '../../config/assignmentForm.schema';
import useFormTableFields from '../../../../shared/data/useFormTableFields';
import { listDealsCustomFields } from '../../services/deals-custom-fields.service';
import DataTable from '../../../../shared/components/patterns/DataTable';
import React from 'react';
import { Check, X } from '../../../../shared/assets/AppIcons';
import EmptyAssignmentsState from '@/modules/deals/components/assignments/EmptyAssignmentsState';
import { IconActionButton, StatusPill } from '@/shared/components/patterns';
import {
  formatDate,
  idsMatch,
  isAssignmentAssignedToCurrentUser,
  isAssignmentRequestedByCurrentUser,
} from '@/modules/deals/utils';
function getPriorityTone(urgency) {
  if (urgency === 'alta') return 'danger';
  if (urgency === 'media') return 'review';
  if (urgency === 'bassa') return 'signed';
  return 'neutral';
}
function getPriorityLabel(urgency) {
  const labels = {
    alta: 'Alta',
    media: 'Media',
    bassa: 'Bassa',
  };
  return labels[urgency] || urgency || '—';
}
function getInitials(value = '') {
  const parts = String(value).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'NA';
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}
export default function AssignmentList({
  assignments,
  emptyState,
  currentUser,
  assignmentActionStates = {},
  showActions = false,
  canDelete = false,
  canReject = false,
  canComplete = false,
  onDelete,
  onAccept,
  onReject,
  onComplete,
  onEdit,
  onCreate,
  onOpenDetail,
}) {
  const tableFields = useFormTableFields(assignmentFormSchema.id, listDealsCustomFields);
  if (!assignments.length) {
    return (
      <EmptyAssignmentsState
        icon={emptyState.icon}
        title={emptyState.title}
        description={emptyState.description}
        showButton={emptyState.showButton}
        onCreate={onCreate}
      />
    );
  }
  return (
    <div className="assignments-registry">
      <DataTable
        formSchema={assignmentFormSchema}
        customFields={tableFields.definitions}
        error={tableFields.error}
        ariaLabel="Task"
        rows={assignments}
        columns={[
          {
            id: 'title',
            render: (assignment) => {
              return (
                <>
                  <div className="assignments-table__task">
                    <strong>{assignment.title || assignment.description}</strong>
                  </div>
                </>
              );
            },
          },
          {
            id: 'recipients',
            render: (assignment) => {
              return (
                <>
                  <div className="assignments-table__person">
                    <span className="assignments-table__avatar">
                      {getInitials(assignment.recipients?.[0])}
                    </span>
                    <span>
                      {(assignment.recipients || [])
                        .map((recipient) =>
                          recipient === currentUser.name ? `${recipient} (Tu)` : recipient
                        )
                        .join(', ') || '—'}
                    </span>
                  </div>
                </>
              );
            },
          },
          {
            id: 'sender',
            render: (assignment) => {
              return (
                <>
                  <div className="assignments-table__person">
                    <span className="assignments-table__avatar assignments-table__avatar--sender">
                      {getInitials(assignment.sender)}
                    </span>
                    <span>
                      {String(assignment.sender || '').trim() || 'Mittente non disponibile'}
                      {idsMatch(assignment.senderId, currentUser?.id) ? ' (Tu)' : ''}
                    </span>
                  </div>
                </>
              );
            },
          },
          {
            id: 'linked',
            header: 'Collegamento',
            order: 3,
            render: (assignment) => {
              return <>{assignment.clientName || assignment.supplierName || 'Task generale'}</>;
            },
          },
          {
            id: 'dueDate',
            render: (assignment) => {
              return <>{formatDate(assignment.dueDate)}</>;
            },
          },
          {
            id: 'urgency',
            render: (assignment) => {
              return (
                <>
                  <StatusPill
                    label={getPriorityLabel(assignment.urgency)}
                    tone={getPriorityTone(assignment.urgency)}
                  />
                </>
              );
            },
          },
          {
            id: 'actions',
            header: '',
            render: (assignment) => {
              const actionState = assignmentActionStates[assignment.id] || {};
              const isActionLoading = Boolean(actionState.loading);
              const acceptLabel =
                actionState.action === 'accept' && isActionLoading ? 'Accettazione in corso' : 'Accetta task';
              const rejectLabel =
                actionState.action === 'reject' && isActionLoading ? 'Rifiuto in corso' : 'Rifiuta task';
              const canRespond =
                assignment.status === 'da_accettare' &&
                isAssignmentAssignedToCurrentUser(assignment, currentUser) &&
                !isAssignmentRequestedByCurrentUser(assignment, currentUser);
              return (
                <>
                  <div className="assignments-table__actions">
                    {showActions && canRespond ? (
                      <IconActionButton
                        icon={Check}
                        tone="success"
                        label={acceptLabel}
                        disabled={isActionLoading}
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          onAccept(assignment.id);
                        }}
                      />
                    ) : null}
                    {canReject && canRespond ? (
                      <IconActionButton
                        icon={X}
                        tone="danger"
                        label={rejectLabel}
                        disabled={isActionLoading}
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          onReject(assignment.id);
                        }}
                      />
                    ) : null}
                    <IconActionButton
                      iconKey="actions.notes"
                      label="Commenta task"
                      onClick={(event) => {
                        event?.stopPropagation?.();
                        onOpenDetail?.(assignment.id, 'comments');
                      }}
                    />
                    {onEdit && isAssignmentRequestedByCurrentUser(assignment, currentUser) ? (
                      <IconActionButton
                        iconKey="actions.edit"
                        label="Modifica task"
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          onEdit(assignment);
                        }}
                      />
                    ) : null}
                    {canComplete && assignment.status === 'in_corso' && isAssignmentAssignedToCurrentUser(assignment, currentUser) ? (
                      <IconActionButton
                        iconKey="actions.complete"
                        tone="success"
                        label="Completa task"
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          onComplete(assignment.id);
                        }}
                      />
                    ) : null}
                    {canDelete && assignment.status === 'da_accettare' ? (
                      <IconActionButton
                        iconKey="actions.delete"
                        label="Elimina task"
                        onClick={(event) => {
                          event?.stopPropagation?.();
                          onDelete(assignment);
                        }}
                      />
                    ) : null}
                  </div>
                  {actionState.error ? (
                    <div className="assignments-table__action-error wt-inline-error" role="alert">
                      {actionState.error}
                    </div>
                  ) : null}
                </>
              );
            },
          },
        ]}
        getRowProps={(assignment) => {
          return {
            tabIndex: 0,
            'aria-haspopup': 'dialog',
            'aria-keyshortcuts': 'Enter Space',
            'aria-label': `Apri dettagli task: ${assignment.title || assignment.description || `Task ${assignment.id}`}`,
            onClick: () => onOpenDetail?.(assignment.id),
            onKeyDown: (event) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onOpenDetail?.(assignment.id);
              }
            },
          };
        }}
      />
    </div>
  );
}
